/**
 * Dashboard API Service
 * Fetches dashboard statistics and analytics data
 */

import axiosClient from '../api/axiosClient';
import { logger } from '../utils/logger';

function resolveShopId(shopId) {
  return shopId || process.env.REACT_APP_SHOP_ID || null;
}

/**
 * Fetch dashboard summary statistics
 */
export const fetchDashboardStats = async (dateRange = 'today', customFrom, customTo, shopId) => {
  try {
    logger.debug('Fetching dashboard stats');
    const resolvedShopId = resolveShopId(shopId);
    if (!resolvedShopId) {
      throw new Error('Missing shop_id for dashboard stats');
    }
    
    // Calculate date range
    const today = new Date();
    let fromDate, toDate;
    
    if (customFrom && customTo) {
      fromDate = customFrom;
      toDate = customTo;
    } else {
      switch (dateRange) {
        case 'today':
          fromDate = today.toISOString().split('T')[0];
          toDate = today.toISOString().split('T')[0];
          break;
        case '7d':
          const weekForward = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
          fromDate = today.toISOString().split('T')[0];
          toDate = weekForward.toISOString().split('T')[0];
          break;
        case '30d':
          const monthForward = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
          fromDate = today.toISOString().split('T')[0];
          toDate = monthForward.toISOString().split('T')[0];
          break;
        default:
          const defaultWeekForward = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
          fromDate = today.toISOString().split('T')[0];
          toDate = defaultWeekForward.toISOString().split('T')[0];
      }
    }

    try {
      // Use the stats endpoint directly - trust its filtering
      const params = new URLSearchParams();
      params.append('from', fromDate);
      params.append('to', toDate);
      params.append('bucket', 'day');
      params.append('shop_id', resolvedShopId);
      
      logger.debug('Dashboard stats date range', { fromDate, toDate });
      
      const response = await axiosClient.get(`/admin/stats/summary?${params}`);
      logger.debug('Dashboard stats response received');
      
      // Trust the stats endpoint results completely
      return response.data;
      
    } catch (error) {
      logger.warn('Dashboard stats endpoint failed, trying fallback', { message: error?.message, status: error?.response?.status });
      
      // Fallback: Call bookings endpoint and calculate stats manually
      const paramsFallback = new URLSearchParams();
      paramsFallback.append('shop_id', resolvedShopId);
      paramsFallback.append('pageSize', 1000);
      if (fromDate && toDate) {
        paramsFallback.append('from', fromDate);
        paramsFallback.append('to', toDate);
      }
      
      const response = await axiosClient.get(`/admin/bookings?${paramsFallback}`);
        const bookings = response.data.items || response.data || [];
        
      logger.debug('Dashboard stats fallback bookings', { count: bookings.length });
      
      // Calculate stats from bookings
      const totalRevenue = bookings.reduce((sum, booking) => {
        if (booking.price) {
          return sum + (booking.price / 100);
        } else if (booking.Service && booking.Service.price_cents) {
          return sum + (booking.Service.price_cents / 100);
        }
        return sum;
      }, 0);
      
      const uniqueClients = new Set(bookings.map(b => b.client_user_id).filter(Boolean)).size;
      const uniqueServices = new Set(bookings.map(b => b.service_id).filter(Boolean)).size;
      
      // Fetch active barbers count separately (not from bookings)
      let activeBarbersCount = 2; // Default fallback
      try {
        const barbersResponse = await axiosClient.get(`/admin/users?role=barber&shop_id=${resolvedShopId}`);
        const allBarbers = barbersResponse.data.items || barbersResponse.data || [];
        activeBarbersCount = allBarbers.length;
      } catch (barberError) {
        logger.warn('Could not fetch barber count, using default', { message: barberError?.message, status: barberError?.response?.status });
      }
      
        return {
          totals: {
          bookings: bookings.length,
          revenue_cents: totalRevenue * 100,
          clients: uniqueClients
          },
          active_counts: {
          barbers: activeBarbersCount,
          services: uniqueServices
        },
        last_24h: {
          bookings: 0 // Could calculate this from bookings if needed
        },
        averages: {
          booking_value: bookings.length ? totalRevenue / bookings.length : 0,
          rating: 4.5 // Could calculate from ratings if available
        }
      };
    }
    
  } catch (error) {
    logger.error('Error fetching dashboard stats', { message: error?.message, status: error?.response?.status });
    throw error;
  }
};

/**
 * Fetch bookings over time for charts
 */
export const fetchBookingsTimeseries = async (dateRange = 'today', customFrom, customTo, shopId) => {
  try {
      const resolvedShopId = resolveShopId(shopId);
      if (!resolvedShopId) {
        throw new Error('Missing shop_id for bookings timeseries');
      }
      const params = new URLSearchParams();
      params.append('bucket', 'day');
      params.append('shop_id', resolvedShopId);
      
    if (customFrom && customTo) {
      params.append('from', customFrom);
      params.append('to', customTo);
    } else {
      const today = new Date();
      let fromDate, toDate;
      
      switch (dateRange) {
        case 'today':
          fromDate = today.toISOString().split('T')[0];
          toDate = today.toISOString().split('T')[0];
          break;
        case '7d':
          const weekForward = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
          fromDate = today.toISOString().split('T')[0];
          toDate = weekForward.toISOString().split('T')[0];
          break;
        case '30d':
          const monthForward = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
          fromDate = today.toISOString().split('T')[0];
          toDate = monthForward.toISOString().split('T')[0];
          break;
        default:
          fromDate = today.toISOString().split('T')[0];
          toDate = today.toISOString().split('T')[0];
          break;
      }
      
      if (fromDate && toDate) {
      params.append('from', fromDate);
      params.append('to', toDate);
      }
    }
    
    const response = await axiosClient.get(`/admin/stats/bookings-timeseries?${params}`);
    return response.data;
    
    } catch (error) {
    console.error('❌ Error fetching bookings timeseries:', error);
    throw error;
    }
};

  /**
 * Fetch revenue over time for charts
   */
export const fetchRevenueTimeseries = async (dateRange = 'today', customFrom, customTo, shopId) => {
    try {
    const resolvedShopId = resolveShopId(shopId);
    if (!resolvedShopId) {
      throw new Error('Missing shop_id for revenue timeseries');
    }
      const params = new URLSearchParams();
    params.append('bucket', 'day');
    params.append('shop_id', resolvedShopId);
    
    if (customFrom && customTo) {
      params.append('from', customFrom);
      params.append('to', customTo);
    }
    
    const response = await axiosClient.get(`/admin/stats/revenue-timeseries?${params}`);
    return response.data;
    
      } catch (error) {
    console.error('❌ Error fetching revenue timeseries:', error);
    throw error;
  }
};

/**
 * Fetch service mix data for pie charts
 */
export const fetchServiceMix = async (shopId) => {
  try {
      const resolvedShopId = resolveShopId(shopId);
      if (!resolvedShopId) {
        throw new Error('Missing shop_id for service mix');
      }
      const params = new URLSearchParams();
      params.append('shop_id', resolvedShopId);
      
      const response = await axiosClient.get(`/admin/stats/service-mix?${params}`);
    return response.data;
    
    } catch (error) {
    console.error('❌ Error fetching service mix:', error);
    throw error;
  }
};

/**
 * Fetch top barbers performance data
 */
export const fetchTopBarbers = async (shopId) => {
  try {
      const resolvedShopId = resolveShopId(shopId);
      if (!resolvedShopId) {
        throw new Error('Missing shop_id for top barbers');
      }
      const params = new URLSearchParams();
      params.append('shop_id', resolvedShopId);
    
    const response = await axiosClient.get(`/admin/stats/top-barbers?${params}`);
    return response.data;
    
  } catch (error) {
    console.error('❌ Error fetching top barbers:', error);
    throw error;
  }
};

// Additional methods that Dashboard.jsx expects
export const getBookingsOverTime = async (days = 7, customFrom = null, customTo = null, shopId) => {
  const dateRange = days === 7 ? '7d' : days === 30 ? '30d' : 'today';
  return await fetchBookingsTimeseries(dateRange, customFrom, customTo, shopId);
};

export const getRevenueVsBookings = async (days = 7, customFrom = null, customTo = null, shopId) => {
  try {
    // Get bookings data (which already includes both bookings and revenue)
    const bookingsData = await fetchBookingsTimeseries(days === 7 ? '7d' : days === 30 ? '30d' : 'today', customFrom, customTo, shopId);
    
    // If bookingsData is an array with the expected format
    if (Array.isArray(bookingsData)) {
      return bookingsData.map(item => ({
        date: item.date,
        revenue: item.revenue || 0,
        bookings: item.bookings || 0
      }));
    }
    
    // If bookingsData has the old format with labels/data
    const labels = bookingsData.labels || [];
    const bookingsArray = bookingsData.data || [];
    
    return labels.map((date, index) => ({
      date: date,
      revenue: 0, // This would need to come from revenueData in old format
      bookings: bookingsArray[index] || 0
    }));
    
  } catch (error) {
    console.error('❌ Error fetching revenue vs bookings:', error);
    return [];
  }
};

export const getClientSourceMix = async (shopId) => {
  return await fetchServiceMix(shopId);
};

export const getBarberPerformance = async (period = 'month', shopId) => {
  return await fetchTopBarbers(shopId);
};

export const getTodaySchedule = async (shopId) => {
  try {
    const resolvedShopId = resolveShopId(shopId);
    if (!resolvedShopId) {
      throw new Error('Missing shop_id for today schedule');
    }
    const today = new Date().toISOString().split('T')[0];
    const response = await axiosClient.get(`/admin/bookings?from=${today}&to=${today}&shop_id=${resolvedShopId}`);
    return response.data?.items || [];
    } catch (error) {
    console.error('❌ Error fetching today schedule:', error);
      return [];
    }
};

export const getRecentBookings = async (limit = 20, shopId) => {
    try {
      const resolvedShopId = resolveShopId(shopId);
      if (!resolvedShopId) {
        throw new Error('Missing shop_id for recent bookings');
      }
      const params = new URLSearchParams();
      params.append('shop_id', resolvedShopId);
    params.append('pageSize', limit);
      
    const response = await axiosClient.get(`/admin/bookings?${params}`);
    return response.data?.items || [];
    } catch (error) {
    console.error('❌ Error fetching recent bookings:', error);
      return [];
    }
};

export const getAIActivity = async (limit = 10) => {
  try {
    // Fetch recent AI scans/operations
    const response = await axiosClient.get(`/scans?limit=${limit}`);
    const scans = response.data || [];
    
    // Transform scans into activity feed items
    const activities = scans.slice(0, limit).map(scan => {
      // Use scan type or default to Hairchitech for AI-powered tools
      let tool = scan.scan_type || 'Hairchitech';
      
      const clientName = scan.client_name || 'Unknown Client';
      const timeAgo = getTimeAgo(scan.created_at);
      
      return {
        id: scan.id,
        tool: tool.charAt(0).toUpperCase() + tool.slice(1),
        client: clientName,
        timestamp: scan.created_at,
        timeAgo: timeAgo,
        status: scan.status || scan.generation_status || 'completed',
        credits: scan.credits_used || 1
      };
    });
    
    return activities;
  } catch (error) {
    console.error('❌ Error fetching AI activity:', error);
    return [];
  }
};

// Helper function to format time ago
const getTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

export const getSystemAlerts = async () => {
  return [];
};

// Default export for compatibility with existing imports
const dashboardApi = {
  getDashboardStats: fetchDashboardStats,
  fetchDashboardStats,
  getBookingsOverTime,
  getRevenueVsBookings,
  getClientSourceMix,
  getBarberPerformance,
  getTodaySchedule,
  getRecentBookings,
  getAIActivity,
  getSystemAlerts,
  fetchBookingsTimeseries,
  fetchRevenueTimeseries,
  fetchServiceMix,
  fetchTopBarbers
};

export default dashboardApi;
